// EAA-1667: The reusable permission-resolution logic, moved from web-content's
// usePermissions into packages/hooks. Given a raw permission source (effective
// permission codes, merged/default screen rules, and app-computed superadmin +
// login flags), it derives the `has(code)` / `hasScreenAccess(path)` gate. The
// *data* (which codes, which rules) and the superadmin/login predicates stay
// app-side and are passed in — this factory is pure and app-agnostic.
import { findScreenRuleForPath, type ScreenRouteRule } from './screenRules';

/** Resolved permission gate + status, read by `usePermissions()`. */
export interface PermissionState {
    /** Whether a subject is authenticated (drives login-gated UI). */
    hasUser: boolean;
    effectivePermissions: string[];
    permissionSet: Set<string>;
    permissionsLoading: boolean;
    permissionsLoaded: boolean;
    permissionsError: string | null;
    mergedScreenRules: ScreenRouteRule[];
    screenRulesLoaded: boolean;
    screenRulesError: string | null;
    /** True if the current subject holds `code` (or is a bypassing superadmin). */
    hasCatalogPermission: (code: string) => boolean;
    /** True if the current subject may view `pathname` per the screen rules. */
    hasScreenAccess: (pathname: string) => boolean;
    canManageTenantPermissions: boolean;
    /**
     * True when the permission set could not be loaded, so an empty
     * `effectivePermissions` means "unknown", not "none". A UI gate MUST branch
     * on this before denying: rendering a failed load as a denial is
     * indistinguishable to the user from having had their access revoked.
     */
    permissionsUnavailable: boolean;
    /**
     * True when the subject's permissions are SCOPED to an acting tenant that has
     * not been chosen yet, so no set has been requested at all. Like
     * {@link permissionsUnavailable} this is "unknown", not "none" — but the cure is
     * the person picking a tenant, not a retry, so a gate must branch on it
     * separately or it renders a denial for a state that is one click from working.
     */
    actingTenantMissing: boolean;
    /** Re-attempt the permission load (no-op unless the app supplies one). */
    retryPermissions: () => void;
}

/** Raw, app-supplied inputs the pure resolver derives {@link PermissionState} from. */
export interface CreatePermissionStateInput {
    /** Effective permission codes for the subject (e.g. from the API). */
    effectivePermissions: string[];
    /** Screen rules from the API (merged in when loaded and non-empty). */
    screenRules: ScreenRouteRule[] | null;
    /** Static fallback rules used until/unless API rules load. */
    defaultScreenRules: readonly ScreenRouteRule[];
    /** Whether a subject is authenticated (screen access is denied when false). */
    hasUser: boolean;
    /** App-computed superadmin bypass (grants every permission + screen). */
    superAdmin: boolean;
    permissionsLoading: boolean;
    permissionsLoaded: boolean;
    permissionsError: string | null;
    screenRulesLoaded: boolean;
    screenRulesError: string | null;
    /** App-computed (staff or catalog `tenant.tokens.manage`). */
    canManageTenantPermissions: boolean;
    /**
     * App-computed: the subject needs an acting tenant to resolve its permissions
     * and none is selected yet. Omitted by apps with no tenant scope.
     */
    actingTenantMissing?: boolean;
    /** Re-attempt the permission load; omitted by apps with nothing to retry. */
    retryPermissions?: () => void;
}

export function createPermissionState(input: CreatePermissionStateInput): PermissionState {
    const {
        effectivePermissions,
        screenRules,
        defaultScreenRules,
        hasUser,
        superAdmin,
        permissionsLoading,
        permissionsLoaded,
        permissionsError,
        screenRulesLoaded,
        screenRulesError,
        canManageTenantPermissions,
        actingTenantMissing = false,
        retryPermissions,
    } = input;

    const permissionSet = new Set(effectivePermissions);

    const mergedScreenRules =
        screenRulesLoaded && screenRules && screenRules.length > 0
            ? screenRules
            : [...defaultScreenRules];

    const hasCatalogPermission = (code: string): boolean => {
        if (superAdmin) return true;
        return permissionSet.has(code);
    };

    const hasScreenAccess = (pathname: string): boolean => {
        if (!hasUser) return false;
        if (superAdmin) return true;
        const rule = findScreenRuleForPath(mergedScreenRules, pathname);
        if (!rule) return true;
        return rule.anyOf.some((c) => permissionSet.has(c));
    };

    // EAA-1762: a load that FAILED and a subject with genuinely no permissions
    // both arrive here as an empty list. Only the first one is recoverable, and
    // only the caller knows which it was — so record it rather than letting
    // every gate re-derive it (and get it wrong; see ScreenAccessGuard).
    const permissionsUnavailable = Boolean(permissionsError) && effectivePermissions.length === 0;

    return {
        hasUser,
        effectivePermissions,
        permissionSet,
        permissionsLoading,
        permissionsLoaded,
        permissionsError,
        mergedScreenRules,
        screenRulesLoaded,
        screenRulesError,
        hasCatalogPermission,
        hasScreenAccess,
        canManageTenantPermissions,
        permissionsUnavailable,
        actingTenantMissing,
        retryPermissions: retryPermissions ?? (() => undefined),
    };
}
